// =============================================================================
// Project Management module — per-vertical settings (/project-management/settings)
//
// The selected org's PM settings: one card per lifecycle in the registry, each holding that vertical's
// notification inboxes for this org. Super-user only, like the global Settings page these fields moved out
// of. Each card saves on its own.
// =============================================================================

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { Input } from '/imports/ui/Input';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { toastOnError } from '/imports/ui/utils';
import { NoOrgSelectedPage } from '/imports/ui/NoOrgSelectedPage';
import { useFindOne, useUser } from '/imports/hooks';
import { Orgs } from '/imports/api/orgs';
import { projectManagementLifecycles } from '/imports/api/projectManagementLifecycles';
import {
  ProjectManagementVerticalSettingsField, projectManagementVerticalSettingsFields, projectManagementDigestZone,
  projectManagementGetOrgSettings, projectManagementSaveVerticalSettings, projectManagementSendVerticalDocumentDigest,
} from '/imports/api/projectManagement';

const Styled = styled(Page)`
  .hint { color: ${css.grey3}; font-size: 0.9rem; margin: 0 0 1.25rem; }
  .vertical { border: 1px solid ${css.grey7}; border-radius: 0.5rem; padding: 1rem 1.25rem 1.1rem; margin-bottom: 1.25rem; max-width: 56rem; }
  .vertical h2 { display: flex; align-items: baseline; gap: 0.6rem; font-size: 1.05rem; margin: 0 0 0.9rem; }
  .vertical h2 .prefix { color: ${css.grey4}; font-size: 0.8rem; font-weight: normal; letter-spacing: 0.04em; }
  .fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(22rem, 1fr)); gap: 0.75rem 1.5rem; }
  .fields label { display: flex; flex-direction: column; gap: 0.25rem; margin: 0; font-weight: normal; }
  .fields label > span { color: ${css.grey3}; font-size: 0.82rem; }
  .fields input { width: 100%; }
  .fields .with-action { display: flex; gap: 0.5rem; align-items: center; }
  .fields .with-action > div { flex: 1; }
  .fields .with-action button { flex: none; white-space: nowrap; }
  .actions { display: flex; justify-content: flex-end; margin-top: 0.9rem; }
`;

type Values = Partial<Record<ProjectManagementVerticalSettingsField, string>>;

// The inboxes that receive a digest rather than an immediate email get a Send Digest button.
const DIGEST_FIELDS: ProjectManagementVerticalSettingsField[] = ['documentNotifyEmail'];
// Today's Pacific calendar date, YYYY-MM-DD — the day the button sends, so a tester sees what they just did.
const todayPacific = () => new Date().toLocaleDateString('en-CA', { timeZone: projectManagementDigestZone });

const fieldLabel = (field: ProjectManagementVerticalSettingsField): string => {
  switch (field) {
    case 'invoiceNotifyEmail': return strings.SettingsPmInvoiceNotifyEmail();
    case 'invoiceApprovedNotifyEmail': return strings.SettingsPmInvoiceApprovedNotifyEmail();
    case 'documentNotifyEmail': return strings.SettingsPmDocumentNotifyEmail();
    case 'documentApprovedNotifyEmail': return strings.SettingsPmDocumentApprovedNotifyEmail();
  }
};

/** Super-user page: the selected org's notification inboxes for every vertical, one card each. */
export const ProjectManagementSettingsPage = () => {
  const user = useUser();
  const org = useFindOne(() => Orgs.find(user?.orgId), [user?.orgId]);
  const [saved, setSaved] = useState<{ [lifecycleKey: string]: Values }>({});
  const [draft, setDraft] = useState<{ [lifecycleKey: string]: Values }>({});
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string>();
  const [sending, setSending] = useState<string>();
  useEffect(() => {
    if (!user?.isSuperUser || !user.orgId) return;
    setLoaded(false);
    toastOnError(async () => {
      const settings = await projectManagementGetOrgSettings.call();
      const byKey: { [lifecycleKey: string]: Values } = {};
      for (const [lifecycleKey, inboxes] of Object.entries(settings?.verticals || {})) {
        byKey[lifecycleKey] = Object.fromEntries(projectManagementVerticalSettingsFields.map((f) => [f, inboxes[f] || '']));
      }
      setSaved(byKey);
      setDraft(byKey);
      setLoaded(true);
    })();
  }, [user?.isSuperUser, user?.orgId]);
  if (!user?.isSuperUser) return <NotPermittedPage />;
  if (!user.orgId) return <NoOrgSelectedPage />;

  const valueOf = (key: string, field: ProjectManagementVerticalSettingsField) => draft[key]?.[field] || '';
  const dirty = (key: string) => projectManagementVerticalSettingsFields.some((f) => (draft[key]?.[f] || '').trim() !== (saved[key]?.[f] || '').trim());
  const setField = (key: string, field: ProjectManagementVerticalSettingsField, value: string) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  const save = (key: string) => toastOnError(async () => {
    setBusy(key);
    try {
      const values = Object.fromEntries(projectManagementVerticalSettingsFields.map((f) => [f, (draft[key]?.[f] || '').trim()])) as Values;
      await projectManagementSaveVerticalSettings.call({ lifecycleKey: key, values });
      setSaved((s) => ({ ...s, [key]: values }));
      setDraft((d) => ({ ...d, [key]: values }));
      toast.success(strings.Saved());
    }
    finally {
      setBusy(undefined);
    }
  })();

  const sendDigest = (key: string) => toastOnError(async () => {
    setSending(key);
    try {
      const r = await projectManagementSendVerticalDocumentDigest.call({ lifecycleKey: key, day: todayPacific() });
      if (r.sent && r.to) toast.success(strings.PmDigestSent(r.to, r.day));
      else toast.info(strings.PmDigestNothingToSend(r.day));
    }
    finally {
      setSending(undefined);
    }
  })();

  return (
    <Styled className={`page${!loaded ? ' initializing' : ''}`}>
      <PageHeading left={<h1>{strings.ProjectManagementSettings()}</h1>} />
      <PageBody>
        <p className="hint">{strings.PmSettingsHint(org?.name || '')}</p>
        {
          Object.values(projectManagementLifecycles).map((lifecycle) => (
            <section className="vertical" key={lifecycle.key}>
              <h2>{lifecycle.label}<span className="prefix">{lifecycle.idPrefix}</span></h2>
              <div className="fields">
                {
                  projectManagementVerticalSettingsFields.map((field) => (
                    <label key={field}>
                      <span>{fieldLabel(field)}</span>
                      {
                        DIGEST_FIELDS.includes(field)
                          ? (
                            <div className="with-action">
                              <Input value={valueOf(lifecycle.key, field)} onChange={(v) => setField(lifecycle.key, field, v || '')} />
                              <Button
                                className="secondary compact"
                                disabled={!(saved[lifecycle.key]?.[field] || '').trim() || dirty(lifecycle.key) || sending === lifecycle.key}
                                tip={strings.SendDigestTip()}
                                onClick={(e) => { e.preventDefault(); sendDigest(lifecycle.key); }}
                              >
                                {strings.SendDigest()}
                              </Button>
                            </div>
                          )
                          : <Input value={valueOf(lifecycle.key, field)} onChange={(v) => setField(lifecycle.key, field, v || '')} />
                      }
                    </label>
                  ))
                }
              </div>
              <div className="actions">
                <Button className="compact" disabled={!dirty(lifecycle.key) || busy === lifecycle.key} onClick={() => save(lifecycle.key)}>{strings.Save()}</Button>
              </div>
            </section>
          ))
        }
      </PageBody>
    </Styled>
  );
};
